import { useState, useEffect } from 'react';
import { Button, Input, Drawer, Tag, Tooltip, Switch, InputNumber, Select, message, Upload, Popconfirm, Divider, Dropdown, Modal, Segmented } from 'antd';
import {
    ArrowLeftOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    PlusOutlined,
    ThunderboltOutlined,
    MessageOutlined,
    SettingOutlined,
    UserAddOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    TagOutlined,
    EditOutlined,
    DeleteOutlined,
    PlayCircleOutlined,
    LinkOutlined,
    UploadOutlined
} from '@ant-design/icons';
import AddStepModal from './AddStepModal';
import ActionBuilder from './ActionBuilder';
import ChatPreview from './ChatPreview';


export default function LinearFlowView({ automation, onBack, onSave, onPublish, onDiscard, loading, automations = [] }) {
    const [flowData, setFlowData] = useState(null);
    const [showAddStep, setShowAddStep] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false); // New state for popup
    const [insertIndex, setInsertIndex] = useState(-1);
    const [editingStep, setEditingStep] = useState(null);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        console.log("======flow data======", flowData)
    }, [flowData])

    // Parse automation data into Quick DM format
    useEffect(() => {
        if (automation) {
            setFlowData(parseAutomationToQuickDM(automation));
        }
    }, [automation]);

    const handleNameChange = (name) => {
        setFlowData(prev => ({ ...prev, name }));
    };

    const handleTriggerChange = (trigger) => {
        setFlowData(prev => ({ ...prev, trigger }));
    };

    const handleAddStep = (stepType, config) => {
        const newStep = createStep(stepType, config);
        setFlowData(prev => {
            const steps = [...(prev.steps || [])];
            if (insertIndex >= 0) {
                steps.splice(insertIndex, 0, newStep);
            } else {
                steps.push(newStep);
            }
            return { ...prev, steps };
        });
        setShowAddStep(false);
        setInsertIndex(-1);
    };

    const handleUpdateStep = (index, updatedStep) => {
        setFlowData(prev => {
            const steps = [...prev.steps];
            steps[index] = updatedStep;
            return { ...prev, steps };
        });
        setEditingStep(null);
    };

    const handleDeleteStep = (index) => {
        setFlowData(prev => {
            const steps = prev.steps.filter((_, i) => i !== index);
            return { ...prev, steps };
        });
    };

    const handleMoveStepUp = (index) => {
        if (index === 0) return;
        setFlowData(prev => {
            const steps = [...prev.steps];
            [steps[index - 1], steps[index]] = [steps[index], steps[index - 1]];
            return { ...prev, steps };
        });
    };

    const handleMoveStepDown = (index) => {
        setFlowData(prev => {
            if (index >= prev.steps.length - 1) return prev;
            const steps = [...prev.steps];
            [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];
            return { ...prev, steps };
        });
    };

    const handleConfirmationChange = (text) => {
        setFlowData(prev => ({
            ...prev,
            confirmation: { ...prev.confirmation, text }
        }));
    };

    const handleSave = () => {
        if (!validateTrigger()) return;
        onSave(flowData);
    };

    const handlePublish = () => {
        if (!validateTrigger()) return;
        onPublish(flowData);
    };

    const validateTrigger = () => {
        if (!flowData.trigger?.keywords?.length && !flowData.trigger?.automate_all) {
            messageApi.error('Please add at least one trigger keyword');
            return false;
        }
        return true;
    };

    const handleToggleLive = (live) => {
        setFlowData(prev => ({ ...prev, live }));
    };

    if (!flowData) return null;

    return (
        <div className="flow-split-container">
            {contextHolder}

            {/* Left Panel: Chat Preview */}
            <div className="flow-preview-panel">
                <ChatPreview flowData={flowData} />
            </div>

            {/* Right Panel: Editor */}
            <div className="flow-editor-panel">
                <div className="linear-flow-view">

                    {/* Header */}
                    <div className="flow-header">
                        <div className="flow-header-left">
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={onBack}
                                type="text"
                            />
                            <Input
                                value={flowData.name}
                                onChange={(e) => handleNameChange(e.target.value)}
                                bordered={false}
                                style={{ fontSize: 24, fontWeight: 600, width: 'auto' }}
                                placeholder="Flow Name"
                            />
                            <span className={`flow-status-badge ${flowData.live ? 'active' : 'inactive'}`}>
                                {flowData.live ? '● LIVE' : '○ DRAFT'}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <Popconfirm
                                title="Discard Draft Changes?"
                                description="This will revert to the live version. Unsaved changes will be lost."
                                onConfirm={onDiscard}
                                okText="Discard"
                                cancelText="Cancel"
                            >
                                <Button danger>Discard Draft</Button>
                            </Popconfirm>
                            <Button
                                type="primary"
                                onClick={() => setShowSaveModal(true)}
                                loading={loading}
                            >
                                Save
                            </Button>
                        </div>
                    </div>

                    {/* Flow Timeline */}
                    <div className="flow-timeline">
                        {/* Entry Point */}
                        <div className="flow-section">
                            <div className="flow-section-marker trigger">
                                <ThunderboltOutlined />
                            </div>
                            <div className="flow-section-label">ENTRY POINT</div>
                            <div className="flow-section-card">
                                <TriggerEditor
                                    trigger={flowData.trigger}
                                    onChange={handleTriggerChange}
                                    automations={automations}
                                    currentId={flowData.id}
                                />
                            </div>
                        </div>

                        {/* Steps */}
                        {flowData.steps?.map((step, index) => (
                            <div key={step.id || index}>
                                {/* Add Step Button Between */}
                                <button
                                    className="add-step-button"
                                    onClick={() => {
                                        setInsertIndex(index);
                                        setShowAddStep(true);
                                    }}
                                >
                                    <PlusOutlined /> Add Step
                                </button>

                                {/* Step Card */}
                                <div className="flow-section">
                                    <div className={`flow-section-marker ${getStepMarkerClass(step.type)}`}>
                                        {getStepIcon(step.type)}
                                    </div>
                                    <div className="flow-section-label">
                                        {getStepLabel(step.type)}
                                    </div>
                                    <div
                                        className="flow-section-card"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                            // Don't open drawer if clicking on inline editor or for button templates
                                            if (e.target.closest('.inline-template-editor')) return;
                                            if (step.messageType === 'button' || step.type === 'message' || step.type === 'delay' || step.type === 'tag') return;
                                            setEditingStep(index);
                                        }}
                                    >
                                        <StepPreview
                                            step={step}
                                            index={index}
                                            totalSteps={flowData.steps.length}
                                            onEdit={() => setEditingStep(index)}
                                            onDelete={() => handleDeleteStep(index)}
                                            onMoveUp={() => handleMoveStepUp(index)}
                                            onMoveDown={() => handleMoveStepDown(index)}
                                            onUpdateStep={(updatedStep) => handleUpdateStep(index, updatedStep)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Add Step Button at End */}
                        <button
                            className="add-step-button"
                            onClick={() => {
                                setInsertIndex(-1);
                                setShowAddStep(true);
                            }}
                        >

                            <PlusOutlined /> Add Step
                        </button>
                        {/* Save/Publish Modal */}
                        <Modal
                            title="Save Automation"
                            open={showSaveModal}
                            onCancel={() => setShowSaveModal(false)}
                            footer={null}
                            centered
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 0' }}>
                                <p style={{ margin: 0, color: '#666' }}>
                                    How would you like to save this automation?
                                </p>
                                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                                    <Button
                                        onClick={() => {
                                            handleSave();
                                            setShowSaveModal(false);
                                        }}
                                        loading={loading}
                                    >
                                        Save to Draft
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            handlePublish();
                                            setShowSaveModal(false);
                                        }}
                                        loading={loading}
                                    >
                                        Publish & Activate
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                    </div>

                    {/* Add Step Modal */}
                    <AddStepModal
                        open={showAddStep}
                        onClose={() => {
                            setShowAddStep(false);
                            setInsertIndex(-1);
                        }}
                        onAdd={handleAddStep}
                    />

                    {/* Edit Step Drawer */}
                    <Drawer
                        title="Edit Step"
                        open={editingStep !== null}
                        onClose={() => setEditingStep(null)}
                        width={500}
                    >
                        {editingStep !== null && flowData.steps[editingStep] && (
                            <ActionBuilder
                                step={flowData.steps[editingStep]}
                                onSave={(updated) => handleUpdateStep(editingStep, updated)}
                                onCancel={() => setEditingStep(null)}
                            />
                        )}
                    </Drawer>

                </div> {/* Close linear-flow-view */}
            </div> {/* Close flow-editor-panel */}




        </div >
    );
}

// Typing Animation Component for Keywords
function TypingKeywords({ keywords }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [displayText, setDisplayText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (keywords.length <= 1) return;

        const currentKeyword = keywords[currentIndex];
        const typingSpeed = isDeleting ? 50 : 100;
        const pauseTime = isDeleting ? 200 : 1500;

        let timeout;

        if (!isDeleting && displayText === currentKeyword) {
            // Pause before deleting
            timeout = setTimeout(() => setIsDeleting(true), pauseTime);
        } else if (isDeleting && displayText === '') {
            // Move to next keyword
            setIsDeleting(false);
            setCurrentIndex((prev) => (prev + 1) % keywords.length);
        } else {
            // Type or delete
            timeout = setTimeout(() => {
                if (isDeleting) {
                    setDisplayText(currentKeyword.substring(0, displayText.length - 1));
                } else {
                    setDisplayText(currentKeyword.substring(0, displayText.length + 1));
                }
            }, typingSpeed);
        }

        return () => clearTimeout(timeout);
    }, [displayText, isDeleting, currentIndex, keywords]);

    // Reset when keywords change
    useEffect(() => {
        setCurrentIndex(0);
        setDisplayText('');
        setIsDeleting(false);
    }, [keywords]);

    if (keywords.length === 0) {
        return <span style={{ color: '#888' }}>Not set</span>;
    }

    if (keywords.length === 1) {
        return <span style={{ color: '#7c3aed' }}>'{keywords[0]}'</span>;
    }

    return (
        <span style={{ color: '#7c3aed' }}>
            '{displayText}'
            <span style={{
                borderRight: '2px solid #7c3aed',
                animation: 'blink 1s step-end infinite',
                marginLeft: 1
            }} />
        </span>
    );
}

// Trigger Editor Component
function TriggerEditor({ trigger, onChange, automations = [], currentId }) {
    const keywords = trigger?.keywords || [];
    const [inputValue, setInputValue] = useState('');
    const automateAll = trigger?.automate_all || false;
    const oneTimeOnly = trigger?.one_time_only !== false; // Default to true

    const handleAddKeyword = () => {
        if (inputValue && !keywords.includes(inputValue)) {
            const newKeywords = [...keywords, inputValue];
            onChange({ ...trigger, keywords: newKeywords });
            setInputValue('');
        }
    };

    const handleRemoveKeyword = (keyword) => {
        const newKeywords = keywords.filter(k => k !== keyword);
        onChange({ ...trigger, keywords: newKeywords });
    };

    const handleAutomateAllChange = (value) => {
        const isTurningOn = value === 'true';

        if (isTurningOn) {
            // Check for duplicate "Any Message" flow
            const hasDuplicate = automations.some(auto => {
                if (auto.id === currentId) return false;

                // Check multiple possible locations for the 'automate_all' flag
                const isAnyInTrigger = auto.trigger?.automate_all === true;
                const isAnyInNodes = auto.nodes?.[0]?.data?.auto_reply?.dm_reply?.automate_all === 'true';
                const isAnyInCleanData = auto.cleanAutomationData?.trigger?.automate_all === true;

                return isAnyInTrigger || isAnyInNodes || isAnyInCleanData;
            });

            if (hasDuplicate) {
                message.error('An "Any Message" trigger flow already exists. Only one can be created per account.');
                return; // Prevent selection
            }
        }

        onChange({ ...trigger, automate_all: isTurningOn });
    };

    const handleOneTimeOnlyChange = (value) => {
        onChange({ ...trigger, one_time_only: value });
    };

    return (
        <div>
            {/* Trigger Type Toggle */}
            <div style={{ marginBottom: 16 }}>
                <p style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>
                    Triggers when user sends DM on Instagram
                </p>
                <Segmented
                    value={automateAll ? 'true' : 'false'}
                    onChange={handleAutomateAllChange}
                    options={[
                        { label: 'Custom Keywords', value: 'false' },
                        { label: 'Any Message', value: 'true' }
                    ]}
                    block
                />
            </div>

            {/* Show Rate Limiting when "Any Message" is selected */}
            {automateAll ? (
                <div style={{ marginBottom: 16, padding: 12, background: '#f9f0ff', borderRadius: 8 }}>
                    <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
                        ⏱️ Select Interval
                    </p>
                    <Segmented
                        value={oneTimeOnly}
                        onChange={handleOneTimeOnlyChange}
                        options={[
                            { label: 'First time only', value: true },
                            { label: 'Once a day', value: false }
                        ]}
                        block
                        style={{ marginBottom: 8 }}
                    />
                    <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                        {oneTimeOnly
                            ? 'Your contacts will receive this automation only for the first time.'
                            : 'Your contacts will receive this automation once per day.'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Keyword Display with Typing Animation */}
                    <div style={{ marginBottom: 12 }}>
                        <strong>{keywords.length > 1 ? 'Keywords: ' : 'Keyword: '}</strong>
                        <TypingKeywords keywords={keywords} />
                        {keywords.length > 1 && (
                            <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>
                                ({keywords.length} keywords)
                            </span>
                        )}
                    </div>

                    {/* Keyword Input */}
                    <div style={{ marginBottom: 12 }}>
                        <Input
                            placeholder="Add trigger keyword and Press Enter"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onPressEnter={handleAddKeyword}
                            suffix={
                                <Button type="primary" size="small" onClick={handleAddKeyword}>
                                    + Add
                                </Button>
                            }
                        />
                    </div>

                    {/* Keyword Tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {keywords.map(keyword => (
                            <Tag
                                key={keyword}
                                closable
                                onClose={() => handleRemoveKeyword(keyword)}
                                color="purple"
                            >
                                {keyword}
                            </Tag>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function InlineTagEditor({ step, onUpdateStep }) {
    const [allTags, setAllTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [creatingLink, setCreatingLink] = useState(false);

    // Fetch tags when component mounts
    useEffect(() => {
        const fetchTags = async () => {
            setLoadingTags(true);
            try {
                const { fireDB } = await import('../../../funcs/firebase');
                const { collection, getDocs, query, orderBy } = await import('firebase/firestore');

                const user_detail = JSON.parse(localStorage.getItem("userData"));
                if (!user_detail?.user_id) return;

                const tagsRef = collection(fireDB, "users", user_detail.user_id, "tags");
                const q = query(tagsRef, orderBy("created_at", "desc"));
                const querySnapshot = await getDocs(q);

                const fetchedTags = querySnapshot.docs.map(doc => ({
                    label: doc.data().name,
                    value: doc.id,
                    name: doc.data().name
                }));
                setAllTags(fetchedTags);
            } catch (error) {
                console.error("Error fetching tags:", error);
            } finally {
                setLoadingTags(false);
            }
        };

        fetchTags();
    }, []);

    const createNewTag = async (e) => {
        if (e) e.preventDefault();
        if (!newTagName.trim()) return;

        setCreatingLink(true);
        try {
            const { fireDB } = await import('../../../funcs/firebase');
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');

            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (!user_detail?.user_id) return;

            const tagsRef = collection(fireDB, "users", user_detail.user_id, "tags");
            const docRef = await addDoc(tagsRef, {
                name: newTagName,
                created_at: serverTimestamp()
            });

            const newTag = { label: newTagName, value: docRef.id, name: newTagName };
            setAllTags(prev => [newTag, ...prev]);

            // Auto-select
            const newTags = [...(step.tags || []), docRef.id];
            const newTagsData = [...(step.tagsData || []), { id: docRef.id, name: newTagName }];
            onUpdateStep({ ...step, tags: newTags, tagsData: newTagsData });

            setNewTagName('');
        } catch (error) {
            console.error("Error creating tag:", error);
        } finally {
            setCreatingLink(false);
        }
    };

    return (
        <div style={{ padding: '8px 4px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <TagOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontWeight: 500, color: '#722ed1' }}>Add Tags</span>
            </div>
            <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select tags"
                value={step.tags || []}
                loading={loadingTags}
                options={allTags}
                onChange={(values, options) => {
                    const tagsData = options.map(opt => ({
                        id: opt.value,
                        name: opt.label || opt.name
                    }));
                    onUpdateStep({ ...step, tags: values, tagsData });
                }}
                bordered={false}
                className="inline-tag-select"
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <div style={{ padding: '0 8px 4px' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Input
                                    style={{ flex: 1 }}
                                    value={newTagName}
                                    onChange={e => setNewTagName(e.target.value)}
                                    placeholder="Create new tag"
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation();
                                            createNewTag(e);
                                        }
                                    }}
                                />
                                <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={createNewTag}
                                    loading={creatingLink}
                                    disabled={!newTagName.trim()}
                                >
                                    Add
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            />
        </div>
    );
}

// Inline Button Template Editor Component - Visual Card Design
function InlineButtonTemplateEditor({
    template,
    templateIndex,
    onUpdate,
    onUpdateButton,
    onAddButton,
    onRemoveButton,
    onRemoveTemplate,
    onUploadImage
}) {
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingButtonIndex, setEditingButtonIndex] = useState(null);

    return (
        <div
            className="inline-template-editor"
            style={{
                background: '#f5f5f5',
                borderRadius: 16,
                padding: 16,
                position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Delete Card Button */}
            <Popconfirm
                title="Delete this card?"
                onConfirm={() => onRemoveTemplate()}
                okText="Yes"
                cancelText="No"
            >
                <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                />
            </Popconfirm>

            {/* Cover Image */}
            {template.thumb_url ? (
                <div style={{ marginBottom: 12, position: 'relative' }}>
                    <img
                        src={template.thumb_url}
                        alt="Cover"
                        style={{
                            width: '100%',
                            maxWidth: 150,
                            maxHeight: 150,
                            aspectRatio: '1/1',
                            objectFit: 'cover',
                            borderRadius: 12
                        }}
                    />
                    <Popconfirm
                        title="Remove this image?"
                        onConfirm={() => onUpdate('thumb_url', '')}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(255,255,255,0.9)', borderRadius: 6 }}
                        />
                    </Popconfirm>
                </div>
            ) : (
                <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                        if (onUploadImage) {
                            onUploadImage(file, templateIndex);
                        }
                        return false;
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            maxWidth: 150,
                            maxHeight: 150,
                            aspectRatio: '1/1',
                            background: '#e8e8e8',
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 12,
                            cursor: 'pointer',
                            border: '2px dashed #d9d9d9'
                        }}
                    >
                        <div style={{ textAlign: 'center', color: '#888' }}>
                            <UploadOutlined style={{ fontSize: 24, marginBottom: 4 }} />
                            <div style={{ fontSize: 12 }}>Upload Image</div>
                        </div>
                    </div>
                </Upload>
            )}

            {/* Title - Click to Edit */}
            {editingTitle ? (
                <Input
                    autoFocus
                    value={template.title || ''}
                    onChange={(e) => onUpdate('title', e.target.value)}
                    onBlur={() => setEditingTitle(false)}
                    onPressEnter={() => setEditingTitle(false)}
                    placeholder="Enter title..."
                    style={{
                        fontWeight: 600,
                        fontSize: 15,
                        marginBottom: 4,
                        background: '#fff',
                        borderRadius: 8
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <div
                    onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }}
                    style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#000',
                        marginBottom: 4,
                        cursor: 'pointer'
                    }}
                >
                    {template.title || 'Click to edit title'}
                </div>
            )}

            {/* Description - Click to Edit */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ marginBottom: 12 }}
            >
                <Input.TextArea
                    value={template.description || ''}
                    onChange={(e) => onUpdate('description', e.target.value)}
                    placeholder="Click to add description"
                    bordered={false}
                    autoSize={{ minRows: 1 }}
                    style={{
                        fontSize: 14,
                        color: '#666',
                        padding: 0,
                        resize: 'none'
                    }}
                />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {template.buttons?.map((btn, btnIndex) => (
                    <div key={btnIndex}>
                        {editingButtonIndex === btnIndex ? (
                            <div style={{
                                background: '#fff',
                                borderRadius: 10,
                                padding: 12,
                                border: '1px solid #e8e8e8'
                            }}>
                                <Input
                                    autoFocus
                                    placeholder="Button text"
                                    value={btn.title || ''}
                                    onChange={(e) => onUpdateButton(btnIndex, 'title', e.target.value)}
                                    style={{ marginBottom: 8, borderRadius: 6 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <Input
                                    placeholder="https://..."
                                    value={btn.url || ''}
                                    onChange={(e) => onUpdateButton(btnIndex, 'url', e.target.value)}
                                    prefix={<LinkOutlined />}
                                    style={{ marginBottom: 8, borderRadius: 6 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    {(template.buttons?.length || 0) > 1 && (
                                        <Popconfirm
                                            title="Delete this button?"
                                            onConfirm={() => { onRemoveButton(btnIndex); setEditingButtonIndex(null); }}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Delete
                                            </Button>
                                        </Popconfirm>
                                    )}
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={(e) => { e.stopPropagation(); setEditingButtonIndex(null); }}
                                    >
                                        Done
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div
                                onClick={(e) => { e.stopPropagation(); setEditingButtonIndex(btnIndex); }}
                                style={{
                                    background: '#fff',
                                    borderRadius: 10,
                                    padding: '10px 16px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    color: '#000',
                                    border: '1px solid #e8e8e8'
                                }}
                            >
                                {btn.title || 'Click to edit button'}
                            </div>
                        )}
                    </div>
                ))}

                {/* Add More Button */}
                {(template.buttons?.length || 0) < 3 && (
                    <div
                        onClick={(e) => { e.stopPropagation(); onAddButton(); }}
                        style={{
                            background: '#fff',
                            borderRadius: 10,
                            padding: '10px 16px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            color: '#888',
                            border: '1px dashed #d9d9d9'
                        }}
                    >
                        + Add button
                    </div>
                )}
            </div>
        </div>
    );
}

// Step Preview Component
function StepPreview({ step, index, totalSteps, onEdit, onDelete, onMoveUp, onMoveDown, onUpdateStep }) {
    const renderContent = () => {
        switch (step.type) {
            case 'message':
                return (
                    <div onClick={e => e.stopPropagation()}>


                        {step.messageType === 'image' ? (
                            step.image_url ? (
                                <div style={{ marginBottom: 12, position: 'relative' }}>
                                    <img
                                        src={step.image_url}
                                        alt="Preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: 200,
                                            borderRadius: 8,
                                            objectFit: 'cover'
                                        }}
                                    />
                                    <Popconfirm
                                        title="Remove this image?"
                                        onConfirm={() => onUpdateStep({ ...step, image_url: '' })}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            style={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                background: 'rgba(255,255,255,0.9)',
                                                borderRadius: 6
                                            }}
                                        />
                                    </Popconfirm>
                                </div>
                            ) : (
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={async (file) => {
                                        try {
                                            const axios = (await import('axios')).default;
                                            const extension = file.name.split('.').pop();
                                            const path = `msg-${Date.now()}`;
                                            const presignedRes = await axios.get(
                                                `https://4iav7nkzgsu2jbpidrdt7seiri0lcfhk.lambda-url.ap-south-1.on.aws/generate_image_url?post_id=${path}&ext=${extension}&custom_name=file`,
                                                { headers: { Authorization: localStorage.getItem('token') } }
                                            );
                                            await axios.put(presignedRes.data.url, file, { headers: { 'Content-Type': file.type } });
                                            const imageUrl = `https://public-assets.instantdm.com/instantdm/public/${path}/media/file.${extension}`;
                                            onUpdateStep({ ...step, image_url: imageUrl });
                                        } catch (error) {
                                            console.error('Failed to upload image:', error);
                                        }
                                        return false;
                                    }}
                                >
                                    <Button icon={<UploadOutlined />} style={{ marginBottom: 12 }} block>
                                        Add Image
                                    </Button>
                                </Upload>
                            )
                        ) : (
                            <Input.TextArea
                                value={step.text || ''}
                                onChange={(e) => onUpdateStep({ ...step, text: e.target.value })}
                                placeholder="Type your message..."
                                bordered={false}
                                autoSize={{ minRows: 2 }}
                                style={{
                                    fontSize: 15,
                                    padding: 0,
                                    resize: 'none'
                                }}
                            />
                        )}

                        {step.templates?.length > 0 && (
                            <div className="card-carousel" style={{ marginTop: 12 }}>
                                {step.templates.map((tpl, tplIndex) => (
                                    <InlineButtonTemplateEditor
                                        key={tpl.id || tplIndex}
                                        template={tpl}
                                        templateIndex={tplIndex}
                                        onUpdate={(field, value) => {
                                            const templates = [...step.templates];
                                            templates[tplIndex] = { ...templates[tplIndex], [field]: value };
                                            onUpdateStep({ ...step, templates });
                                        }}
                                        onUpdateButton={(btnIndex, field, value) => {
                                            const templates = [...step.templates];
                                            const buttons = [...(templates[tplIndex].buttons || [])];
                                            buttons[btnIndex] = { ...buttons[btnIndex], [field]: value };
                                            templates[tplIndex] = { ...templates[tplIndex], buttons };
                                            onUpdateStep({ ...step, templates });
                                        }}
                                        onAddButton={() => {
                                            const templates = [...step.templates];
                                            const buttons = [...(templates[tplIndex].buttons || [])];
                                            if (buttons.length < 3) {
                                                buttons.push({ title: '', type: 'web_url', url: '' });
                                                templates[tplIndex] = { ...templates[tplIndex], buttons };
                                                onUpdateStep({ ...step, templates });
                                            }
                                        }}
                                        onRemoveButton={(btnIndex) => {
                                            const templates = [...step.templates];
                                            const buttons = templates[tplIndex].buttons.filter((_, i) => i !== btnIndex);
                                            templates[tplIndex] = { ...templates[tplIndex], buttons };
                                            onUpdateStep({ ...step, templates });
                                        }}
                                        onRemoveTemplate={() => {
                                            const templates = step.templates.filter((_, i) => i !== tplIndex);
                                            onUpdateStep({ ...step, templates });
                                        }}
                                        onUploadImage={async (file, idx) => {
                                            try {
                                                const axios = (await import('axios')).default;
                                                const extension = file.name.split('.').pop();
                                                const path = `tpl-${Date.now()}`;

                                                // Get presigned URL
                                                const presignedRes = await axios.get(
                                                    `https://4iav7nkzgsu2jbpidrdt7seiri0lcfhk.lambda-url.ap-south-1.on.aws/generate_image_url?post_id=${path}&ext=${extension}&custom_name=file`,
                                                    { headers: { Authorization: localStorage.getItem('token') } }
                                                );

                                                // Upload to S3
                                                await axios.put(presignedRes.data.url, file, {
                                                    headers: { 'Content-Type': file.type }
                                                });

                                                const imageUrl = `https://public-assets.instantdm.com/instantdm/public/${path}/media/file.${extension}`;
                                                const templates = [...step.templates];
                                                templates[idx] = { ...templates[idx], thumb_url: imageUrl };
                                                onUpdateStep({ ...step, templates });
                                            } catch (error) {
                                                console.error('Failed to upload image:', error);
                                            }
                                        }}
                                    />
                                ))}
                                {/* Add Card Button */}
                                {(step.templates?.length || 0) < 10 && (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const templates = [...(step.templates || [])];
                                            templates.push({
                                                id: `tpl-${Date.now()}`,
                                                title: '',
                                                description: '',
                                                thumb_url: '',
                                                buttons: [{ title: '', type: 'web_url', url: '' }]
                                            });
                                            onUpdateStep({ ...step, templates });
                                        }}
                                        style={{
                                            minHeight: 250,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            border: '2px dashed #d9d9d9',
                                            color: '#888'
                                        }}
                                        className="inline-template-editor"
                                    >
                                        <PlusOutlined style={{ marginRight: 8 }} />
                                        Add Card
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );

            case 'question':
                return (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={{
                                background: '#e8f5e9',
                                color: '#2e7d32',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 12
                            }}>
                                {step.questionType?.toUpperCase() || 'TEXT'}
                            </span>
                            <span style={{
                                background: '#e6f7ff',
                                color: '#1890ff',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4
                            }}>
                                ⏳ We'll wait for user response
                            </span>
                        </div>
                        {step.questionType === 'custom_input' ? (
                            <div onClick={e => e.stopPropagation()}>
                                <Input.TextArea
                                    value={step.question || ''}
                                    onChange={(e) => onUpdateStep({ ...step, question: e.target.value })}
                                    placeholder="Enter your question..."
                                    bordered={false}
                                    autoSize={{ minRows: 1 }}
                                    style={{
                                        fontSize: 15,
                                        padding: 0,
                                        resize: 'none',
                                        width: '100%'
                                    }}
                                />
                            </div>
                        ) : (
                            <p style={{ margin: 0 }}>{step.question || 'No question set'}</p>
                        )}
                    </div>
                );

            case 'delay':
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                        <ClockCircleOutlined style={{ color: '#f59e0b' }} />
                        <span style={{ fontWeight: 500 }}>Wait</span>
                        <InputNumber
                            size="small"
                            min={1}
                            max={60}
                            value={step.duration || 1}
                            onChange={(val) => onUpdateStep({ ...step, duration: val, durationType: 'm' })}
                            style={{ width: 60 }}
                        />
                        <span>minutes</span>
                    </div>
                );

            case 'tag':
                return <InlineTagEditor step={step} onUpdateStep={onUpdateStep} />;

            default:
                return <span>Unknown step type</span>;
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            {renderContent()}
            <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                display: 'flex',
                gap: 4
            }}>
                {/* Reorder buttons */}
                <Tooltip title="Move up">
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                        style={{ opacity: index === 0 ? 0.3 : 1 }}
                    />
                </Tooltip>
                <Tooltip title="Move down">
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === totalSteps - 1}
                        onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                        style={{ opacity: index === totalSteps - 1 ? 0.3 : 1 }}
                    />
                </Tooltip>
                {step.messageType !== 'button' && step.type !== 'message' && step.type !== 'delay' && step.type !== 'tag' && (
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Delete">
                    <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    />
                </Tooltip>
            </div>
        </div>
    );
}

// Helper Functions
function parseAutomationToQuickDM(automation) {
    // If already in Quick DM format
    if (automation.trigger && automation.steps) {
        return automation;
    }

    // Parse from Flow Editor format
    const triggerNode = automation.nodes?.find(n => n.type === 'triggerComp');
    const autoReply = triggerNode?.data?.auto_reply || {};

    // Determine trigger type
    let triggerType = 'dm_reply';
    let keywords = [];
    if (autoReply.dm_reply?.enabled) {
        triggerType = 'dm_reply';
        keywords = autoReply.dm_reply.triger_words || [];
    } else if (autoReply.story_reply?.enabled) {
        triggerType = 'story_reply';
        keywords = autoReply.story_reply.triger_words || [];
    } else if (autoReply.comment_reply?.enabled) {
        triggerType = 'comment_reply';
        keywords = autoReply.comment_reply.triger_words || [];
    }

    // Parse steps
    const steps = [];
    automation.nodes?.forEach((node, index) => {
        if (index === 0) return; // Skip trigger

        if (node.type === 'messageReply') {
            node.data?.messages?.forEach(msg => {
                if (msg.type === 'delay') {
                    steps.push({
                        id: msg.id,
                        type: 'delay',
                        duration: msg.delayDuration || 3,
                        durationType: msg.durationType || 's'
                    });
                } else {
                    steps.push({
                        id: msg.id,
                        type: 'message',
                        messageType: msg.type,
                        text: msg.text,
                        image_url: msg.image_url,
                        templates: msg.templates,
                        smartReplies: msg.smartReplies
                    });
                }
            });
        } else if (node.type === 'questionNode') {
            node.data?.messages?.forEach(q => {
                steps.push({
                    id: q.id,
                    type: 'question',
                    questionType: q.question_type,
                    question: q.question,
                    skip_option: q.skip_option,
                    retry_attempt: q.retry_attempt,
                    wrong_input: q.wrong_input,
                    variable: q.custom_variable_name
                });
            });
        }
    });

    // Get confirmation from last message node
    const lastMessageNode = [...(automation.nodes || [])]
        .reverse()
        .find(n => n.type === 'messageReply');
    const confirmationText = lastMessageNode?.data?.messages?.[0]?.text || '';

    return {
        id: automation.id,
        name: automation.name || 'Untitled Flow',
        live: automation.live || false,
        trigger: {
            type: triggerType,
            keywords,
            automate_all: autoReply[triggerType]?.automate_all === 'true',
            one_time_only: autoReply[triggerType]?.one_time_only !== false
        },
        steps,
        confirmation: {
            text: confirmationText
        },
        welcome_message: autoReply.welcome_message
    };
}

function createStep(type, config = {}) {
    const id = `step-${Date.now()}`;

    switch (type) {
        case 'text':
            return { id, type: 'message', messageType: 'text', text: '', ...config };
        case 'image':
            return { id, type: 'message', messageType: 'image', image_url: '', ...config };
        case 'button':
            return {
                id,
                type: 'message',
                messageType: 'button',
                templates: [{
                    id: `tpl-${Date.now()}`,
                    title: '',
                    description: '',
                    thumb_url: '',
                    buttons: [{ title: '', type: 'web_url', url: '' }]
                }],
                ...config
            };
        case 'image':
            return {
                id,
                type: 'message',
                messageType: 'image',
                image_url: '',
                ...config
            };
        case 'product':
            return {
                id,
                type: 'message',
                messageType: 'button',
                templates: [{
                    id: `tpl-${Date.now()}`,
                    title: '',
                    description: '',
                    thumb_url: '',
                    buttons: [{ title: '', type: 'web_url', url: '' }]
                }],
                isProductCard: true,
                ...config
            };
        case 'delay':
            return { id, type: 'delay', duration: 1, durationType: 'm', ...config };
        case 'tag':
            return { id, type: 'tag', tags: [], tagsData: [], ...config };
        case 'email':
            return { id, type: 'question', questionType: 'email', question: 'What\'s your email?', ...config };
        case 'phone':
            return { id, type: 'question', questionType: 'phone', question: 'What\'s your phone number?', ...config };
        case 'name':
            return { id, type: 'question', questionType: 'name', question: 'What\'s your name?', ...config };
        case 'custom_input':
            return { id, type: 'question', questionType: 'custom_input', question: 'Enter your custom question...', ...config };
        default:
            return { id, type: 'message', messageType: 'text', text: '', ...config };
    }
}

function getStepMarkerClass(type) {
    switch (type) {
        case 'message': return 'message';
        case 'question': return 'lead';
        case 'delay': return 'logic';
        case 'tag': return 'logic';
        default: return 'message';
    }
}

function getStepIcon(type) {
    switch (type) {
        case 'message': return <MessageOutlined />;
        case 'question': return <UserAddOutlined />;
        case 'delay': return <ClockCircleOutlined />;
        case 'tag': return <TagOutlined />;
        default: return <MessageOutlined />;
    }
}

function getStepLabel(type) {
    switch (type) {
        case 'message': return 'MESSAGE STEP';
        case 'question': return 'LEAD GEN PHASE';
        case 'delay': return 'FLOW LOGIC';
        case 'tag': return 'FLOW LOGIC';
        default: return 'STEP';
    }
}
